import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { APPEARANCES, LAYOUTS } from './constants';
import getAppearanceConfig from './helpers/get-appearance-config';
import Icon from '../icon';
import Box from '../box';
import Stack from '../stack';
import Heading from '../heading';
import Paragraph from '../paragraph';
import ActionButton from '../action-button';
import IconButton from '../icon-button';
import { iconsList } from '../icon/glyphs';

const MOBILE_BREAKPOINT = 568;

function CtaBanner({
  iconGlyph = null,
  ctaButton = null,
  appearance = APPEARANCES.defaultCta,
  layout = LAYOUTS.default,
  onClose = null,
  title,
  subtitle = null,
  roundedCorners = true,
  centered = false,
  animate = true,
  onAnimationEnd = null,
}) {
  const [computedLayout, setComputedLayout] = useState(layout);
  const rootElementRef = useRef(null);

  useEffect(() => {
    const { current: animatedElement } = rootElementRef;

    if (animate && onAnimationEnd) {
      animatedElement.addEventListener('animationend', onAnimationEnd, false);
    }

    return () => {
      if (animate && onAnimationEnd) {
        animatedElement.removeEventListener('animationend', onAnimationEnd, false);
      }
    };
  }, [animate, onAnimationEnd]);

  useEffect(() => {
    function computeLayout() {
      const isMobileViewportMatched = window.matchMedia(
        `(max-width: ${MOBILE_BREAKPOINT}px)`
      ).matches;

      if (isMobileViewportMatched) {
        if (computedLayout !== LAYOUTS.mobile) {
          setComputedLayout(LAYOUTS.mobile);
        }

        return;
      }

      if (layout !== computedLayout) {
        setComputedLayout(layout);
      }
    }

    computeLayout();

    if (layout !== LAYOUTS.mobile) {
      window.addEventListener('resize', computeLayout, false);
    }

    return () => {
      if (layout !== LAYOUTS.mobile) {
        window.removeEventListener('resize', computeLayout, false);
      }
    };
  }, [layout, computedLayout]);

  const {
    iconType,
    actionButtonAppearance,
    actionButtonFormat,
    headingAppearance,
    paragraphAppearance,
  } = getAppearanceConfig(appearance);
  const rootNodeClass = classNames(
    'ws-ui-cta-banner',
    `ws-ui-cta-banner--${appearance}`,
    `ws-ui-cta-banner--layout-${computedLayout}`,
    {
      'ws-ui-cta-banner--rounded-corners': roundedCorners,
      'ws-ui-cta-banner--animated': animate,
    }
  );

  const contentContainerClass = classNames('ws-ui-cta-banner__content-container', {
    'ws-ui-cta-banner__content-container--centered': centered && computedLayout !== LAYOUTS.mobile,
  });

  return (
    <div className={rootNodeClass} ref={rootElementRef}>
      <Box
        paddingLeft="spacing-s"
        paddingRight="spacing-s"
        paddingTop={computedLayout === LAYOUTS.mobile ? 'spacing-s' : 'spacing-xs'}
        paddingBottom={computedLayout === LAYOUTS.mobile ? 'spacing-s' : 'spacing-xs'}
      >
        <div className={contentContainerClass}>
          <div
            className={classNames(
              'ws-ui-cta-banner__content',
              `ws-ui-cta-banner__content--layout-${computedLayout}`
            )}
          >
            <div className="ws-ui-cta-banner__text-container">
              {iconGlyph && (
                <div
                  className={classNames(
                    'ws-ui-cta-banner__icon-wrapper',
                    `ws-ui-cta-banner__icon-wrapper--layout-${computedLayout}`
                  )}
                >
                  <Icon glyph={iconGlyph} type={iconType} />
                </div>
              )}
              <div className="ws-ui-cta-banner__text-wrapper">
                <Stack gap="spacing-4-xs">
                  <Heading type="heading-6" appearance={headingAppearance}>
                    {title}
                  </Heading>
                  {subtitle && <Paragraph appearance={paragraphAppearance}>{subtitle}</Paragraph>}
                </Stack>
              </div>
            </div>

            {ctaButton && ctaButton.onClick && (
              <div
                className={classNames(
                  'ws-ui-cta-banner__button-wrapper',
                  `ws-ui-cta-banner__button-wrapper--layout-${computedLayout}`
                )}
              >
                <ActionButton
                  onClick={ctaButton.onClick}
                  appearance={actionButtonAppearance}
                  label={ctaButton.label}
                  format={actionButtonFormat}
                  fullWidth={computedLayout === LAYOUTS.mobile}
                />
              </div>
            )}
          </div>

          {onClose && (
            <div className="ws-ui-cta-banner__close">
              <IconButton glyph="close" size="small" onClick={onClose} transparent />
            </div>
          )}
        </div>
      </Box>
    </div>
  );
}

CtaBanner.propTypes = {
  appearance: PropTypes.oneOf(Object.values(APPEARANCES)),
  layout: PropTypes.oneOf(Object.values(LAYOUTS)),
  ctaButton: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  iconGlyph: PropTypes.oneOf([...iconsList, null]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.string,
  roundedCorners: PropTypes.bool,
  centered: PropTypes.bool,
  animate: PropTypes.bool,
  onAnimationEnd: PropTypes.func,
  onClose: PropTypes.func,
};

export default CtaBanner;
